import { useState, useCallback } from "react";
import { S3Client, PutObjectCommand, ListObjectsV2Command, GetObjectCommand } from "@aws-sdk/client-s3";
import { ICustomMetadata } from "../interfaces/Chat";
import { ChimeCredentials, PatientChatsMap } from "../context/ChatContext";

interface UseChatFilesProps {
  patientId: number | null;
  patientChats: PatientChatsMap | null;
  getCredentials: () => Promise<ChimeCredentials>; // ✅ receive this from ChatContext
  sendMessage: (content: string, customMetadata?: ICustomMetadata) => Promise<void>;
  channelArn: string | null;
}

export interface S3File {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
}

export const useChatFiles = ({ patientId, patientChats, getCredentials, sendMessage, channelArn }: UseChatFilesProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!channelArn) return setError("No active chat channel.");
      if (!patientId || !patientChats) return setError("No patient selected.");

      setLoading(true);
      setError(null);

      try {
        // 1 Get credentials from parent (ChatContext)
        const creds = await getCredentials();

        if (!creds.attachments_s3_bucket) throw new Error("S3 bucket not found.");

        // 2 Compute file key (chat prefix + timestamp + original name)
        const chatPrefix = patientChats[patientId]?.attachments_s3_key;
        if (!chatPrefix) throw new Error("attachments_s3_key missing for this chat.");
        const fileKey = `${chatPrefix}/${Date.now()}-${file.name}`;

        // 3 Create S3 client
        const s3 = new S3Client({
          region: creds.attachments_s3_bucket_region,
          credentials: {
            accessKeyId: creds.access_key_id,
            secretAccessKey: creds.secret_access_key,
            sessionToken: creds.session_token,
          },
        });

        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // 4 Upload file
        await s3.send(
          new PutObjectCommand({
            Bucket: creds.attachments_s3_bucket,
            Key: fileKey,
            Body: uint8Array,
            ContentType: file.type,
          })
        );

        console.log("Uploaded file:", fileKey);

        // 5 Send chat message with file metadata
        const customMetadata: ICustomMetadata = {
          attachments: [
            {
              mimeType: file.type,
              originalName: file.name,
              size: file.size,
              s3Key: fileKey,
            }
          ]
        }
        await sendMessage(`[File] ${file.name}`, customMetadata);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Upload failed");
      } finally {
        setLoading(false);
      }
    },
    [channelArn, patientId, patientChats, getCredentials, sendMessage]
  );

  const listFiles = useCallback(async (): Promise<S3File[]> => {
    if (!patientId || !patientChats) return [];

    try {
      const creds = await getCredentials();
      const chatPrefix = patientChats[patientId]?.attachments_s3_key;
      if (!chatPrefix) return [];

      const s3 = new S3Client({
        region: creds.attachments_s3_bucket_region,
        credentials: {
          accessKeyId: creds.access_key_id,
          secretAccessKey: creds.secret_access_key,
          sessionToken: creds.session_token,
        },
      });

      const response = await s3.send(
        new ListObjectsV2Command({
          Bucket: creds.attachments_s3_bucket,
          Prefix: chatPrefix,
        })
      );

      const ret = (response.Contents || []).map(item => ({
        key: item.Key!,
        name: item.Key!.split("/").pop() || "",
        size: item.Size!,
        lastModified: item.LastModified!,
      }));
      console.log("Listed files:", ret);
      return ret;
    } catch (err) {
      console.error("Failed to list files:", err);
      return [];
    }
  }, [patientId, patientChats, getCredentials]);

  const downloadFile = useCallback(async (fileKey: string): Promise<Blob | null> => {
        try {
            const creds = await getCredentials();

            const s3 = new S3Client({
                region: creds.attachments_s3_bucket_region,
                credentials: {
                accessKeyId: creds.access_key_id,
                secretAccessKey: creds.secret_access_key,
                sessionToken: creds.session_token,
                },
            });

            const response = await s3.send(
                new GetObjectCommand({
                Bucket: creds.attachments_s3_bucket,
                Key: fileKey,
                })
            );

            if (!response.Body) return null;

            // Convert ReadableStream to ArrayBuffer
            const reader = (response.Body as ReadableStream<Uint8Array>).getReader();
            const chunks: Uint8Array[] = [];
            let done = false;

            while (!done) {
                const { value, done: readerDone } = await reader.read();
                if (value) chunks.push(value);
                done = readerDone;
            }

            // Merge chunks into single Uint8Array
            const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
            const array = new Uint8Array(totalLength);
            let offset = 0;
            for (const chunk of chunks) {
                array.set(chunk, offset);
                offset += chunk.length;
            }

            // Create a valid Blob
            const blob = new Blob([array], { type: response.ContentType || undefined });
            return blob;
        } catch (err) {
            console.error("Failed to download file:", err);
            return null;
        }
    }, [getCredentials]);

  return { uploadFile, listFiles, downloadFile, loading, error };
};